/**
 * Générateur — Attendu 953 — « Caractériser le parallélisme par les angles :
 * angles alternes internes, angles correspondants. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Angles et parallélisme
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/953.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Configuration unique : deux droites (d₁), (d₂) coupées par une sécante (s),
 *  trois droites, deux points d'intersection A et B, huit secteurs angulaires
 *  (sauf niveau 8 : trapèze, deux sécantes, cf. Non tranché). Figures par
 *  construction exacte (svgAngles) : l'angle affiché est l'angle dessiné.
 *
 *  Réglage transverse de la spec : deux régimes de nommage. « directions » =
 *  droites nommées (d₁), (d₂), (s), angles marqués par une couleur ou un numéro
 *  de secteur ; « points » = points E, F sur (d₁), G, H sur (d₂), P, Q sur la
 *  sécante, angles nommés à trois lettres.
 *
 *  1. position       — niveau 1 : reconnaître si deux angles marqués sont alternes
 *                      internes, correspondants, ou ni l'un ni l'autre (QCM à trois
 *                      propositions ; « ni l'un ni l'autre » est une réponse
 *                      réellement engendrée : opposés, adjacents, alternes externes
 *                      non nommés, même côté intérieurs ou extérieurs).
 *  2. reciproque     — niveau 2 : les droites sont-elles parallèles ? angles marqués
 *                      par leur mesure, écart de 1 ou 2 degrés quand non parallèles
 *                      (QCM Oui / Non / On ne peut pas conclure ; drag = réponse
 *                      binaire + couple d'angles invoqué).
 *  3. decision_nommee — niveau 3 : même décision, angles désignés par leur nom à
 *                      trois lettres, non marqués (QCM + drag).
 *  4. calcul1        — niveau 4, zéro étape : calculer un angle, parallélisme donné
 *                      (codé par des flèches), angles marqués (saisie, drag, QCM à
 *                      quatre propositions dont le supplémentaire ; régime « sans
 *                      hypothèse » du QCM : le parallélisme n'est pas donné, la seule
 *                      réponse est « on ne peut pas le savoir »).
 *  5. calcul2        — niveau 4, une étape : passage par l'angle opposé par le sommet
 *                      ou par le supplémentaire (saisie, drag, QCM).
 *  6. calcul_nomme   — niveau 5 : même calcul, angles désignés par leur nom (saisie,
 *                      QCM ; zéro ou une étape).
 *  7. paires         — niveau 6 : désigner sur la figure (secteurs numérotés ①…⑧)
 *                      l'angle alterne interne ou correspondant d'un angle donné
 *                      (QCM ; relier = tous les correspondants, ou les deux couples
 *                      d'alternes internes avec deux cibles leurres).
 *  8. nommer         — niveau 7 : nommer à trois lettres l'angle alterne interne ou
 *                      correspondant d'un angle donné (saisie, les deux ordres de
 *                      lettres admis ; QCM avec le sommet mal placé).
 *  9. trapeze        — niveau 8 : synthèse close, deux parallèles coupées par deux
 *                      sécantes ; les deux sens se combinent : angles alternes
 *                      internes égaux → parallélisme, puis parallélisme → mesure d'un
 *                      angle de la figure (drag, saisie, QCM). Ni somme des angles
 *                      d'un quadrilatère, ni nature d'un parallélogramme.
 * 10. caracterisation — la propriété caractéristique énoncée comme une équivalence
 *                      (écart assumé n° 2 et point de vigilance n° 1 de la spec) :
 *                      les deux implications, puis le mot « équivalence » (drag).
 *  Niveau 9 (mélange) : `generate()` sans clé.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - nombre de droites : 3 (deux droites + une sécante) sur toutes les cartes,
 *     sauf la synthèse (niveau 8 : trapèze, deux sécantes, prescrit par
 *     graduation_difficulte et objets_geometriques) ;
 *   - inclinaison de la sécante : entier 0..180 (libre) ; le trapèze garde ses
 *     bases quasi horizontales (cadre) ;
 *   - mesure de l'angle marqué : entier 25..155, jamais 90 ;
 *   - écart de non-parallélisme : 1 ou 2 degrés, appliqué à la direction de (d₂)
 *     entière (cohérent pour les quatre angles du sommet B) ;
 *   - distance des points d'intersection : 3,0 à 6,0 unités (1 unité = 24 px),
 *     A et B jamais à égale distance du centre de la figure ;
 *   - étapes intermédiaires : 0 (calcul1, calcul_nomme direct, trapeze) ou 1
 *     (calcul2, calcul_nomme, régimes « même côté » de la décision) ;
 *   - mesures à connaître par cœur : hors périmètre des cartes (aucun rappel).
 *  Exclusions : aucun angle droit, plat ou nul ; alternes externes jamais nommés
 *   (ils sont « ni l'un ni l'autre ») ; deux secteurs marqués toujours distincts ;
 *   écart de non-parallélisme jamais > 2 ; rien à mesurer au rapporteur ; aucun
 *   distracteur égal à la bonne réponse (vérifié par construction, QCM à textes
 *   distincts) ; pas de carte « parallèle / perpendiculaire sans mesure ».
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. croire que deux angles marqués forment nécessairement un couple
 *      → `couple_force` (position, paires, reciproque, decision_nommee, calcul2)
 *   2. conclure au parallélisme d'après l'aspect de la figure
 *      → `aspect_figure` (reciproque, decision_nommee, trapeze)
 *   3. confondre angles égaux et supplémentaires, rendre 180 − mesure
 *      → `confusion_egaux_supplementaires` (paires, calcul1, calcul2, calcul_nomme,
 *        reciproque, decision_nommee, trapeze, caracterisation)
 *   4. appliquer la propriété à un couple qui ne la vérifie pas, sans détour
 *      → `sans_detour` (calcul2, calcul_nomme, reciproque, decision_nommee)
 *   5. confondre alternes internes et correspondants
 *      → `confusion_alternes_correspondants` (position, paires, calcul1, calcul2,
 *        calcul_nomme, nommer, reciproque, decision_nommee, trapeze)
 *   6. prendre la perception pour une preuve
 *      → `perception_preuve` (reciproque, decision_nommee, calcul1, calcul_nomme)
 *   7. ne connaître la caractérisation que dans un sens
 *      → `un_seul_sens` (calcul1, calcul2, calcul_nomme, trapeze)
 *   8. nommer un angle à trois lettres avec le sommet ailleurs qu'au milieu
 *      → `sommet_mal_place` (nommer : QCM, et `erreurVisee` de la saisie)
 *
 *  Graduation → cartes : 1 position · 2 reciproque · 3 decision_nommee ·
 *   4 calcul1, calcul2 · 5 calcul_nomme · 6 paires · 7 nommer · 8 trapeze ·
 *   9 generate() sans clé · transverse : régimes « directions » / « points ».
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - `nombre_de_droites_de_la_configuration` = 3 « toujours » contredit la
 *     configuration close de synthèse (deux sécantes) ; la synthèse suit
 *     graduation_difficulte ;
 *   - « les deux angles marqués ne sont jamais identiques » est lu « jamais le
 *     même secteur » (deux angles alternes internes de droites parallèles sont
 *     égaux par nature) ;
 *   - `nombre_de_questions_par_serie` et le cadre de justification rédigée en
 *     sortie papier relèvent de l'application, pas du générateur ;
 *   - la carte `caracterisation` n'est sélectionnée par aucun niveau mais
 *     répond à l'écart assumé « nommer l'équivalence dans chaque série ».
 */

import {
    randInt, randomChoice, pickLettresSains,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier, filtrerDistracteurs,
    subscriptToSvgTspan,
} from '../../../utils';
import {
    creerFigure, ptAt, inter, secteursAutour,
    secteurCorrespondant, secteurAlterneInterne, secteurOppose,
    imgElement, CERCLES, VERT, ROUGE, BLEU,
} from './svgAngles';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 953;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_POSITION = 'Nommer la position de deux angles';
const L_RECIP    = 'Les droites sont-elles parallèles ?';
const L_DECISION = 'Décider avec des angles nommés';
const L_CALCUL1  = 'Premier calcul d\'angle';
const L_CALCUL2  = 'Calculer un angle en justifiant';
const L_CALC_NOM = 'Calculer un angle nommé';
const L_PAIRES   = 'Repérer les paires d\'angles';
const L_NOMMER   = 'Nommer l\'angle à trois lettres';
const L_TRAPEZE  = 'Synthèse : le trapèze';
const L_CARACT   = 'La propriété caractéristique';

const VB = { w: 360, h: 280 };
const UNITE = 24;                         // 1 unité de figure = 24 px

const REL_ALT = 'alternes internes';
const REL_COR = 'correspondants';
const REL_NI = 'ni l\'un ni l\'autre';

// Mots-clés des erreurs visées (cf. en-tête).
const E_COUPLE = 'couple_force';
const E_ASPECT = 'aspect_figure';
const E_SUPP = 'confusion_egaux_supplementaires';
const E_DETOUR = 'sans_detour';
const E_ALTCOR = 'confusion_alternes_correspondants';
const E_PERCEP = 'perception_preuve';
const E_SENS = 'un_seul_sens';
const E_SOMMET = 'sommet_mal_place';

/** Pool de lettres pour le régime « points » (I et O exclus : confusables). */
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVXYZ'.split('');

// ═══════════════════════════════════════════════════════════
// TIRAGES ÉLÉMENTAIRES (bornes de la spec)
// ═══════════════════════════════════════════════════════════

/** Mesure de l'angle marqué : entier 25..155, jamais 90 (aucun angle droit). */
const tirerMesure = () => {
    const m = randInt(25, 155);
    return m === 90 ? randomChoice([89, 91]) : m;
};

/** Écart de non-parallélisme : ±1 ou ±2 degrés. */
const tirerEcart = () => randomChoice([-2, -1, 1, 2]);

/** Distance AB en unités (3,0 à 6,0) ; les angles aigus prennent une distance ≥ 5. */
const tirerDistance = (m) => {
    const sinM = Math.sin((m * Math.PI) / 180);
    const min = sinM < 0.6 ? 50 : 30;
    return randInt(min, 60) / 10;
};

const salt = (...n) => n.map((x) => Math.round(x * 10)).join('_');
const norm = (d) => ((d % 360) + 360) % 360;
const rad = (d) => (d * Math.PI) / 180;
const memeDir = (a, b) => { const d = norm(a - b); return d < 0.5 || d > 359.5; };
const deg = (x) => `${x}°`;

// ═══════════════════════════════════════════════════════════
// HELPERS DE FIGURE
// ═══════════════════════════════════════════════════════════

/** Styles d'arc (repli noir et blanc) : donné plein, intermédiaire tireté, cherché pointillé. */
const STYLE = {
    donne: { couleur: VERT, dash: '' },
    inter: { couleur: BLEU, dash: '5 3' },
    cherche: { couleur: ROUGE, dash: '1.5 3' },
    neutre: { couleur: VERT, dash: '' },
    second: { couleur: ROUGE, dash: '1.5 3' },
};

/** Rayon du texte d'une marque : s'éloigne du sommet quand le secteur est étroit. */
const rTexteAuto = (mesure) => Math.min(64, Math.max(34, Math.round(13 / Math.sin(rad(mesure / 2)))));
const rNumeroAuto = (mesure) => Math.min(50, Math.max(26, Math.round(10 / Math.sin(rad(mesure / 2)))));

/** Arc de secteur [a1, a2] au sommet V, avec style (couleur + tireté) et texte sur la bissectrice. */
const arc = (fig, V, sec, texte, style, { r = 18, fs = 12 } = {}) => {
    const n = 12;
    const pts = [];
    for (let i = 0; i <= n; i++) {
        const p = ptAt(V, sec.a1 + ((sec.a2 - sec.a1) * i) / n, r);
        pts.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
    }
    const dash = style.dash ? ` stroke-dasharray="${style.dash}"` : '';
    fig.brut(`<polyline class="ma" points="${pts.join(' ')}" fill="none" stroke="${style.couleur}" stroke-width="2"${dash}/>`);
    if (texte) {
        const T = ptAt(V, (sec.a1 + sec.a2) / 2, rTexteAuto(sec.mesure) + (r - 18));
        fig.brut(`<text class="mv" x="${T.x.toFixed(1)}" y="${(T.y + 4).toFixed(1)}" font-family="sans-serif" font-size="${fs}" fill="${style.couleur}" text-anchor="middle">${texte}</text>`);
    }
};

/** Étiquette d'une droite posée à son extrémité (dans le cadre), décalée de la droite. */
const etiquetteBout = (fig, P, dirDeg, txt) => {
    const u = { x: Math.cos(rad(dirDeg)), y: -Math.sin(rad(dirDeg)) };
    let t = 1e9;
    if (u.x > 1e-9) t = Math.min(t, (VB.w - 22 - P.x) / u.x);
    if (u.x < -1e-9) t = Math.min(t, (22 - P.x) / u.x);
    if (u.y > 1e-9) t = Math.min(t, (VB.h - 16 - P.y) / u.y);
    if (u.y < -1e-9) t = Math.min(t, (16 - P.y) / u.y);
    const E = ptAt(P, dirDeg, Math.max(t, 0));
    const L = ptAt(E, dirDeg + 90, 11);
    fig.brut(`<text x="${L.x.toFixed(1)}" y="${(L.y + 4).toFixed(1)}" font-family="serif" font-style="italic" font-size="13" fill="#334155" text-anchor="middle">${subscriptToSvgTspan(txt)}</text>`);
};

/** Chevron de codage du parallélisme, centré en P, pointant dans la direction dir. */
const chevron = (fig, P, dir) => {
    const T = ptAt(P, dir, 5);
    const W1 = ptAt(T, dir + 150, 9);
    const W2 = ptAt(T, dir - 150, 9);
    fig.brut(`<path class="fl" d="M ${W1.x.toFixed(1)},${W1.y.toFixed(1)} L ${T.x.toFixed(1)},${T.y.toFixed(1)} L ${W2.x.toFixed(1)},${W2.y.toFixed(1)}" fill="none" stroke="#334155" stroke-width="1.8"/>`);
};

const dansCadre = (P, marge = 22) => P.x > marge && P.x < VB.w - marge && P.y > marge && P.y < VB.h - marge;

/** Codage du parallélisme : une flèche identique sur chaque droite, du côté qui reste dans le cadre. */
const coderParalleles = (fig, A, B, dirL, dist) => {
    const sens = [dirL, dirL + 180].find((d) => dansCadre(ptAt(A, d, dist)) && dansCadre(ptAt(B, d, dist)));
    const d = sens == null ? dirL : sens;
    chevron(fig, ptAt(A, d, dist), dirL);
    chevron(fig, ptAt(B, d, dist), dirL);
};

/** Un secteur au sommet, par mesure (0,5° près), intérieur ou non. */
const secteursDe = (secteurs, { interior = null, mesure = null } = {}) =>
    secteurs.filter((s) => (interior == null || s.interior === interior)
        && (mesure == null || Math.abs(s.mesure - mesure) < 0.5));

/**
 * Configuration à une sécante. (d₁) passe par A, (d₂) par B ; la sécante (s)
 * est inclinée de dirT (0..180), (d₁) fait l'angle m avec elle, (d₂) est
 * parallèle à (d₁) à `ecart` près. `nommee` place les points E, F, G, H, P, Q.
 */
const configUneSecante = ({ m, ecart = 0, nommee = false, paralleles = false, s }) => {
    const dirT = randInt(0, 180);
    const sens = randomChoice([1, -1]);
    const dirL = dirT + sens * m;
    const dirL2 = dirL + ecart;
    const D = tirerDistance(m) * UNITE;
    const t = randomChoice([0.36, 0.4, 0.44, 0.56, 0.6, 0.64]);   // jamais 0,5 : figure non symétrique
    const centre = { x: VB.w / 2 + randInt(-8, 8), y: VB.h / 2 + randInt(-6, 6) };
    const A = ptAt(centre, dirT + 180, D * t);
    const B = ptAt(centre, dirT, D * (1 - t));
    const fig = creerFigure(VB, s);
    fig.droite(A, dirL, 'ln1');
    fig.droite(B, dirL2, 'ln2');
    fig.droite(A, dirT, 'tr1');
    const secteursA = secteursAutour(dirL, dirT, dirT);
    const secteursB = secteursAutour(dirL2, dirT, dirT + 180);
    const cfg = { fig, A, B, dirL, dirL2, dirT, m, secteursA, secteursB, nommee, lettres: null, rayonsA: null, rayonsB: null };

    if (nommee) {
        const [lA, lB, E, F, G, H, P, Q] = pickLettresSains(POOL_LETTRES, 8);
        cfg.lettres = { A: lA, B: lB, E, F, G, H, P, Q };
        const dPts = 58;
        const pE = ptAt(A, dirL + 180, dPts), pF = ptAt(A, dirL, dPts);
        const pG = ptAt(B, dirL2 + 180, dPts), pH = ptAt(B, dirL2, dPts);
        const pP = ptAt(A, dirT + 180, 50), pQ = ptAt(B, dirT, 50);
        fig.point(A, lA, dirL + 90 * sens, 14);
        fig.point(B, lB, dirL2 - 90 * sens, 14);
        fig.point(pE, E, dirL + 90, 12); fig.point(pF, F, dirL + 90, 12);
        fig.point(pG, G, dirL2 - 90, 12); fig.point(pH, H, dirL2 - 90, 12);
        fig.point(pP, P, dirT + 90, 12); fig.point(pQ, Q, dirT + 90, 12);
        // `ls` = TOUS les points nommes portes par le rayon. Sur la secante, le rayon
        // qui va de A vers B porte B puis Q, et celui qui va de B vers A porte A puis
        // P : nommer l'angle avec l'un ou l'autre designe le MEME angle. `l` reste le
        // point retenu pour l'affichage, `ls` sert aux reponses acceptees.
        cfg.rayonsA = [
            { dir: dirL + 180, l: E, ls: [E] },
            { dir: dirL, l: F, ls: [F] },
            { dir: dirT, l: lB, ls: [lB, Q] },
            { dir: dirT + 180, l: P, ls: [P] },
        ];
        cfg.rayonsB = [
            { dir: dirL2 + 180, l: G, ls: [G] },
            { dir: dirL2, l: H, ls: [H] },
            { dir: dirT + 180, l: lA, ls: [lA, P] },
            { dir: dirT, l: Q, ls: [Q] },
        ];
        cfg.nomD1 = `(${E}${F})`;
        cfg.nomD2 = `(${G}${H})`;
    } else {
        fig.point(A, 'A', dirL + 90 * sens, 14);
        fig.point(B, 'B', dirL2 - 90 * sens, 14);
        etiquetteBout(fig, A, dirL + 180, '(d₁)');
        etiquetteBout(fig, B, dirL2 + 180, '(d₂)');
        etiquetteBout(fig, A, dirT + 180, '(s)');
        cfg.nomD1 = '(d₁)';
        cfg.nomD2 = '(d₂)';
    }
    if (paralleles) coderParalleles(fig, A, B, dirL, nommee ? 96 : 84);
    return cfg;
};

/** Nom à trois lettres d'un secteur (régime « points ») : lettre du rayon a1, sommet, lettre du rayon a2. */
const nomSecteur = (sec, rayons, sommet) => {
    const l1 = rayons.find((r) => memeDir(r.dir, sec.a1));
    const l2 = rayons.find((r) => memeDir(r.dir, sec.a2));
    if (!l1 || !l2) throw new Error('953 : secteur sans rayons nommés');
    return randomChoice([true, false]) ? [l1.l, sommet, l2.l] : [l2.l, sommet, l1.l];
};
const texAngle = ([a, s, b]) => `$\\widehat{${a}${s}${b}}$`;
const ordreInverse = ([a, s, b]) => [b, s, a];
const sommetMalPlace = ([a, s, b]) => (randomChoice([true, false]) ? [s, a, b] : [a, b, s]);

/** Le secteur de B qui est « l'autre relation » (ou l'opposé du secteur cible), pour coder les distracteurs. */
const secteurAutreRelation = (sV, secteursB, relation) =>
    (relation === REL_ALT ? secteurCorrespondant(sV, secteursB) : secteurAlterneInterne(sV, secteursB))
    || secteurOppose(relation === REL_ALT ? secteurAlterneInterne(sV, secteursB) : secteurCorrespondant(sV, secteursB), secteursB);

const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — position (niveau 1) : QCM à trois propositions
// ═══════════════════════════════════════════════════════════

const CAS_NI = ['oppose', 'adjacent', 'alt_ext', 'co_int', 'co_ext'];

const genPositionQcm = () => {
    const m = tirerMesure();
    const cas = randomChoice(['ai', 'corr', 'ni']);
    const sousCas = cas === 'ni' ? randomChoice(CAS_NI) : cas;
    const cfg = configUneSecante({ m, s: salt(m, randInt(0, 9999)) });
    const { fig, A, B, secteursA, secteursB } = cfg;
    const interieur = ['ai', 'co_int'].includes(sousCas) ? true : (['alt_ext', 'co_ext'].includes(sousCas) ? false : null);
    const sV = randomChoice(secteursDe(secteursA, { interior: interieur }));
    let sR, sommetR = B;
    switch (sousCas) {
        case 'ai': sR = secteurAlterneInterne(sV, secteursB); break;
        case 'corr': sR = secteurCorrespondant(sV, secteursB); break;
        case 'oppose': sR = secteurOppose(sV, secteursA); sommetR = A; break;
        case 'adjacent':
            sR = randomChoice(secteursA.filter((sec) => sec !== sV && (memeDir(sec.a1, sV.a2) || memeDir(sec.a2, sV.a1))));
            sommetR = A; break;
        case 'alt_ext': sR = secteursB.find((sec) => sec.sideT === -sV.sideT && !sec.interior); break;
        case 'co_int': sR = secteursB.find((sec) => sec.sideT === sV.sideT && sec.interior); break;
        default: sR = secteursB.find((sec) => sec.sideT === sV.sideT && !sec.interior); break;   // co_ext
    }
    if (!sR || sR === sV) throw new Error('953 position : secteur rouge introuvable');
    arc(fig, A, sV, '', STYLE.neutre, { r: 20 });
    arc(fig, sommetR, sR, '', STYLE.second, { r: sommetR === A ? 27 : 20 });

    const correct = cas === 'ai' ? REL_ALT : (cas === 'corr' ? REL_COR : REL_NI);
    const codes = {
        [REL_ALT]: cas === 'corr' ? E_ALTCOR : E_COUPLE,
        [REL_COR]: cas === 'ai' ? E_ALTCOR : E_COUPLE,
        [REL_NI]: null,   // troisième proposition prescrite, réponse atteignable (cas « ni »)
    };
    const texte = '<p>Les droites (d₁) et (d₂) sont coupées par la sécante (s). '
        + 'L\'angle vert et l\'angle rouge sont-ils des angles alternes internes, des angles correspondants, ou ni l\'un ni l\'autre ?</p>';
    const payload = makeQcm([REL_ALT, REL_COR, REL_NI], correct, texte, {
        ...opts(L_POSITION), elements_additionnels: imgElement(fig.render()),
    });
    return meta(payload, 'position_qcm_unique', {
        m, cas: sousCas, correct,
        distracteurs: [REL_ALT, REL_COR, REL_NI].filter((r) => r !== correct).map((r) => ({ choix: r, code: codes[r] })),
    });
};

// ═══════════════════════════════════════════════════════════
// Cartes 2 & 3 — reciproque (niveau 2) et decision_nommee (niveau 3)
// ═══════════════════════════════════════════════════════════

const REGIMES_DECISION = ['oui_direct', 'oui_direct', 'non_direct', 'non_direct', 'oui_supp', 'non_supp', 'indecidable'];

/**
 * Tire un régime de décision et construit la figure : deux angles (mA en A,
 * mB en B) en position alternes internes / correspondants (direct), ou du
 * même côté intérieurs (supp : le supplémentaire de mA est alterne interne
 * avec mB), ou tous deux au sommet A (indécidable). Les droites sont dessinées
 * exactement : parallèles ssi la réponse est « oui ».
 */
const construireDecision = ({ nommee }) => {
    const regime = randomChoice(REGIMES_DECISION);
    const relation = randomChoice([REL_ALT, REL_COR]);
    const mA = tirerMesure();
    let ecart = 0;
    if (regime === 'non_direct' || regime === 'non_supp') ecart = tirerEcart();
    const cfg = configUneSecante({ m: mA, ecart, nommee, s: salt(mA, ecart, randInt(0, 9999)) });
    const { fig, A, B, secteursA, secteursB } = cfg;

    // Angle marqué en A : intérieur pour AI / même côté, quelconque pour correspondants.
    const interieurA = regime === 'indecidable' || relation === REL_ALT || regime.endsWith('supp') ? true : null;
    const sA = randomChoice(secteursDe(secteursA, { interior: interieurA, mesure: mA }));
    let sB, sommetB = B, mB, reponse;
    if (regime === 'indecidable') {
        const opp = randomChoice([true, false]);
        sB = opp ? secteurOppose(sA, secteursA)
            : randomChoice(secteursA.filter((sec) => sec !== sA && (memeDir(sec.a1, sA.a2) || memeDir(sec.a2, sA.a1))));
        sommetB = A;
        mB = Math.round(sB.mesure);
        reponse = 'indecidable';
    } else if (regime.endsWith('direct')) {
        sB = relation === REL_ALT ? secteurAlterneInterne(sA, secteursB) : secteurCorrespondant(sA, secteursB);
        mB = Math.round(sB.mesure);
        reponse = regime === 'oui_direct' ? 'oui' : 'non';
    } else {
        sB = secteursB.find((sec) => sec.sideT === sA.sideT && sec.interior);   // même côté, intérieur
        mB = Math.round(sB.mesure);
        reponse = regime === 'oui_supp' ? 'oui' : 'non';
    }
    if (!sB) throw new Error('953 décision : secteur B introuvable');
    // Garde-fous : mesures dans les bornes, jamais 90, jamais égales dans un cas « non ».
    if (mB < 25 || mB > 155 || mB === 90 || mA === 90) return null;
    if (reponse === 'non' && regime === 'non_direct' && mB === mA) return null;
    if (regime.endsWith('supp') && (reponse === 'oui') !== (mA + mB === 180)) return null;
    if (regime.endsWith('direct') && (reponse === 'oui') !== (mA === mB)) return null;

    let nomA = null, nomB = null;
    if (nommee) {
        nomA = nomSecteur(sA, cfg.rayonsA, cfg.lettres.A);
        nomB = nomSecteur(sB, sommetB === A ? cfg.rayonsA : cfg.rayonsB, sommetB === A ? cfg.lettres.A : cfg.lettres.B);
    } else {
        arc(fig, A, sA, deg(mA), STYLE.donne, { r: 18 });
        arc(fig, sommetB, sB, deg(mB), STYLE.donne, { r: sommetB === A ? 26 : 18 });
    }
    return { cfg, fig, regime, relation, mA, mB, reponse, ecart, nomA, nomB, sommetBenA: sommetB === A };
};

const tirerDecision = (o) => {
    for (let i = 0; i < 40; i++) { const d = construireDecision(o); if (d) return d; }
    throw new Error('953 décision : aucun tirage valide');
};

const OUI = 'Oui, elles sont parallèles.';
const NON = 'Non, elles ne sont pas parallèles.';
const INDET = 'On ne peut pas conclure avec ces deux angles.';

/** Phrase des données pour une décision : angles marqués, ou angles nommés. */
const donneesDecision = (d) => {
    if (!d.nomA) return 'Sur la figure, deux angles sont marqués par leur mesure.';
    return `On sait que ${texAngle(d.nomA)} mesure ${deg(d.mA)} et que ${texAngle(d.nomB)} mesure ${deg(d.mB)}. Les angles ne sont pas marqués sur la figure.`;
};

const genDecisionQcm = (nommee) => {
    const d = tirerDecision({ nommee });
    const { cfg, fig } = d;
    const correct = d.reponse === 'oui' ? OUI : (d.reponse === 'non' ? NON : INDET);
    const codes = {};
    if (d.reponse === 'oui') {
        codes[NON] = d.regime === 'oui_supp' ? E_COUPLE : E_SUPP;
        codes[INDET] = d.regime === 'oui_supp' ? E_DETOUR : null;
    } else if (d.reponse === 'non') {
        codes[OUI] = E_ASPECT;
        codes[INDET] = d.regime === 'non_supp' ? E_DETOUR : null;
    } else {
        codes[OUI] = E_PERCEP;
        codes[NON] = E_COUPLE;
    }
    const texte = `<p>${donneesDecision(d)} Les droites ${cfg.nomD1} et ${cfg.nomD2} sont-elles parallèles ?</p>`;
    const payload = makeQcm([OUI, NON, INDET], correct, texte, {
        ...opts(nommee ? L_DECISION : L_RECIP), elements_additionnels: imgElement(fig.render()),
    });
    return meta(payload, nommee ? 'decision_nommee_qcm_unique' : 'reciproque_qcm_unique', {
        regime: d.regime, relation: d.relation, mA: d.mA, mB: d.mB, ecart: d.ecart, reponse: d.reponse,
        distracteurs: [OUI, NON, INDET].filter((c) => c !== correct).map((c) => ({ choix: c, code: codes[c] })),
    });
};

/** Réponse binaire + couple d'angles invoqué (régimes directs seulement). */
const genDecisionDrag = (nommee) => {
    let d = tirerDecision({ nommee });
    for (let i = 0; i < 40 && !d.regime.endsWith('direct'); i++) d = tirerDecision({ nommee });
    if (!d.regime.endsWith('direct')) throw new Error('953 décision drag : régime direct introuvable');
    const { cfg, fig } = d;
    const oui = d.reponse === 'oui';
    const sujet = nommee ? `Les angles ${texAngle(d.nomA)} et ${texAngle(d.nomB)}` : 'Les angles marqués';
    const phrase = `${sujet} sont des angles {{${d.relation}}} et ils {{${oui ? 'ont la même mesure' : 'n\'ont pas la même mesure'}}} : `
        + `les droites ${cfg.nomD1} et ${cfg.nomD2} {{${oui ? 'sont parallèles' : 'ne sont pas parallèles'}}}.`;
    const candidats = [
        { choix: d.relation === REL_ALT ? REL_COR : REL_ALT, code: E_ALTCOR },
        { choix: oui ? 'n\'ont pas la même mesure' : 'ont la même mesure', code: oui ? E_COUPLE : E_ASPECT },
        { choix: oui ? 'ne sont pas parallèles' : 'sont parallèles', code: oui ? E_SUPP : E_ASPECT },
        { choix: 'sont supplémentaires', code: E_SUPP },
    ];
    const distracteurs = filtrerDistracteurs(phrase, candidats.map((c) => c.choix));
    const texte = `<p>${donneesDecision(d)} Complète la conclusion en choisissant les étiquettes :</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(nommee ? L_DECISION : L_RECIP), mode: 'drag', distracteurs, strict: false,
        elements_additionnels: imgElement(fig.render()),
    });
    return meta(payload, nommee ? 'decision_nommee_texte_trou_drag' : 'reciproque_texte_trou_drag', {
        regime: d.regime, relation: d.relation, mA: d.mA, mB: d.mB, ecart: d.ecart, reponse: d.reponse,
        distracteurs: candidats.filter((c) => distracteurs.includes(c.choix)),
    });
};

const genReciproqueQcm = () => genDecisionQcm(false);
const genReciproqueDrag = () => genDecisionDrag(false);
const genDecisionNommeeQcm = () => genDecisionQcm(true);
const genDecisionNommeeDrag = () => genDecisionDrag(true);

// ═══════════════════════════════════════════════════════════
// Cartes 4, 5, 6 — calcul (niveaux 4 et 5)
// ═══════════════════════════════════════════════════════════

/**
 * Construit une situation de calcul : angle donné m en A (vert), angle cherché
 * en B (rouge), parallélisme codé ou non. Modes :
 *   'direct'    : cherché = alterne interne ou correspondant du donné (0 étape) ;
 *   'oppose'    : donné extérieur, cherché = alterne interne de l'opposé du donné (1 étape, = m) ;
 *   'supp'      : donné intérieur, cherché = supplémentaire de l'alterne interne
 *                 ou du correspondant du donné (1 étape, = 180 − m).
 */
const construireCalcul = ({ mode, nommee, paralleles = true, marquerInter = false }) => {
    const m = tirerMesure();
    const relation = randomChoice([REL_ALT, REL_COR]);
    const cfg = configUneSecante({ m, nommee, paralleles, s: salt(m, randInt(0, 9999)) });
    const { fig, A, B, secteursA, secteursB } = cfg;
    let sV, sInter = null, sommetInter = null, sR, reponse, relationUtile = relation;
    if (mode === 'direct') {
        sV = randomChoice(secteursDe(secteursA, { interior: relation === REL_ALT ? true : null, mesure: m }));
        sR = relation === REL_ALT ? secteurAlterneInterne(sV, secteursB) : secteurCorrespondant(sV, secteursB);
        reponse = m;
    } else if (mode === 'oppose') {
        sV = randomChoice(secteursDe(secteursA, { interior: false, mesure: m }));
        sInter = secteurOppose(sV, secteursA); sommetInter = A;
        sR = secteurAlterneInterne(sInter, secteursB);
        relationUtile = REL_ALT;
        reponse = m;
    } else {
        sV = randomChoice(secteursDe(secteursA, { interior: true, mesure: m }));
        sInter = relation === REL_ALT ? secteurAlterneInterne(sV, secteursB) : secteurCorrespondant(sV, secteursB);
        sommetInter = B;
        sR = randomChoice(secteursB.filter((sec) => sec !== sInter && (memeDir(sec.a1, sInter.a2) || memeDir(sec.a2, sInter.a1))));
        reponse = 180 - m;
    }
    if (!sV || !sR) throw new Error('953 calcul : secteurs introuvables');
    if (Math.round(sR.mesure) !== reponse) throw new Error('953 calcul : la figure ne réalise pas la réponse');
    let nomV = null, nomR = null;
    if (nommee) {
        nomV = nomSecteur(sV, cfg.rayonsA, cfg.lettres.A);
        nomR = nomSecteur(sR, cfg.rayonsB, cfg.lettres.B);
    } else {
        arc(fig, A, sV, deg(m), STYLE.donne, { r: 18 });
        if (marquerInter && sInter) arc(fig, sommetInter, sInter, '', STYLE.inter, { r: sommetInter === A ? 27 : 26 });
        arc(fig, B, sR, '?', STYLE.cherche, { r: 18 });
    }
    return { cfg, fig, m, mode, relation, relationUtile, reponse, nomV, nomR };
};

/** Options d'un QCM de calcul (quatre propositions, dont le supplémentaire). */
const OPT_INDET = 'On ne peut pas le savoir : le parallélisme des droites n\'est pas donné.';
const optionsCalcul = (c, sujet) => {
    const angleV = sujet.v, angleR = sujet.r;
    const rel = (r) => `${deg(c.m)} : ${angleV} et ${angleR} sont des angles ${r}, donc de même mesure.`;
    if (c.mode === 'direct') {
        return {
            correct: c.paralleles ? rel(c.relation) : OPT_INDET,
            options: [
                { choix: rel(c.relation), code: c.paralleles ? null : E_PERCEP },
                { choix: rel(c.relation === REL_ALT ? REL_COR : REL_ALT), code: c.paralleles ? E_ALTCOR : E_PERCEP },
                { choix: `${deg(180 - c.m)} : ${angleR} est le supplémentaire de ${angleV}.`, code: E_SUPP },
                { choix: OPT_INDET, code: c.paralleles ? E_SENS : null },
            ],
        };
    }
    if (c.mode === 'oppose') {
        const correct = `${deg(c.m)} : l'angle opposé par le sommet à ${angleV} mesure ${deg(c.m)}, et il est alterne interne avec ${angleR}.`;
        return {
            correct,
            options: [
                { choix: correct, code: null },
                { choix: `${deg(180 - c.m)} : ${angleR} est le supplémentaire de ${angleV}.`, code: E_SUPP },
                { choix: rel(REL_COR), code: E_COUPLE },
                { choix: OPT_INDET, code: E_SENS },
            ],
        };
    }
    const correct = `${deg(180 - c.m)} : l'angle ${c.relation === REL_ALT ? 'alterne interne' : 'correspondant'} de ${angleV} mesure ${deg(c.m)}, et ${angleR} lui est supplémentaire.`;
    return {
        correct,
        options: [
            { choix: correct, code: null },
            { choix: rel(c.relation), code: E_DETOUR },
            { choix: rel(c.relation === REL_ALT ? REL_COR : REL_ALT), code: E_COUPLE },
            { choix: OPT_INDET, code: E_SENS },
        ],
    };
};

const SUJET_MARQUE = { v: 'l\'angle vert', r: 'l\'angle rouge' };

const enonceParalleles = (cfg) => `Les droites ${cfg.nomD1} et ${cfg.nomD2} sont parallèles (codage sur la figure).`;

// ── calcul1 : zéro étape, angles marqués ──

const genCalcul1Saisie = () => {
    const c = construireCalcul({ mode: 'direct', nommee: false });
    const texte = `<p>${enonceParalleles(c.cfg)} L'angle vert mesure ${deg(c.m)}. `
        + 'Combien mesure l\'angle rouge ? (Donne un nombre entier de degrés.)</p>';
    const payload = makeSaisie(String(c.reponse), texte, {
        ...opts(L_CALCUL1), strict: true, suffixe: '°', elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul1_texte', { m: c.m, relation: c.relation, reponse: c.reponse, etapes: 0 });
};

const genCalcul1Drag = () => {
    const c = construireCalcul({ mode: 'direct', nommee: false });
    const phrase = `L'angle vert et l'angle rouge sont des angles {{${c.relation}}}. `
        + `Comme les droites sont parallèles, ils ont la {{même}} mesure : l'angle rouge mesure {{${c.m}}}°.`;
    const candidats = [
        { choix: c.relation === REL_ALT ? REL_COR : REL_ALT, code: E_ALTCOR },
        { choix: 'opposés par le sommet', code: E_COUPLE },
        { choix: 'supplémentaire', code: E_SUPP },
        { choix: String(180 - c.m), code: E_SUPP },
    ];
    const distracteurs = filtrerDistracteurs(phrase, candidats.map((x) => x.choix));
    const texte = `<p>${enonceParalleles(c.cfg)} L'angle vert mesure ${deg(c.m)}. Complète le raisonnement :</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CALCUL1), mode: 'drag', distracteurs, strict: false, elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul1_texte_trou_drag', {
        m: c.m, relation: c.relation, reponse: c.reponse, etapes: 0,
        distracteurs: candidats.filter((x) => distracteurs.includes(x.choix)),
    });
};

const genCalcul1Qcm = () => {
    const paralleles = randInt(1, 4) !== 1;      // un tirage sur quatre : parallélisme non donné
    const c = construireCalcul({ mode: 'direct', nommee: false, paralleles });
    c.paralleles = paralleles;
    const { correct, options } = optionsCalcul(c, SUJET_MARQUE);
    const texte = paralleles
        ? `<p>${enonceParalleles(c.cfg)} L'angle vert mesure ${deg(c.m)}. Que peut-on dire de l'angle rouge ?</p>`
        : `<p>L'énoncé ne dit rien sur les droites ${c.cfg.nomD1} et ${c.cfg.nomD2}, et la figure ne porte aucun codage. L'angle vert mesure ${deg(c.m)}. Que peut-on dire de l'angle rouge ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(L_CALCUL1), elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul1_qcm_unique', {
        m: c.m, relation: c.relation, reponse: paralleles ? c.reponse : null, paralleles, etapes: 0,
        distracteurs: options.filter((o) => o.choix !== correct),
    });
};

// ── calcul2 : une étape (opposé par le sommet, ou supplémentaire) ──

const modeCalcul2 = () => randomChoice(['oppose', 'supp', 'supp']);

const genCalcul2Saisie = () => {
    const c = construireCalcul({ mode: modeCalcul2(), nommee: false });
    const texte = `<p>${enonceParalleles(c.cfg)} L'angle vert mesure ${deg(c.m)}. `
        + 'Combien mesure l\'angle rouge ? (Donne un nombre entier de degrés.)</p>';
    const payload = makeSaisie(String(c.reponse), texte, {
        ...opts(L_CALCUL2), strict: true, suffixe: '°', elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul2_texte', { m: c.m, mode: c.mode, relation: c.relation, reponse: c.reponse, etapes: 1, erreurVisee: c.mode === 'supp' ? E_DETOUR : E_SUPP });
};

const genCalcul2Drag = () => {
    const c = construireCalcul({ mode: modeCalcul2(), nommee: false, marquerInter: true });
    let phrase, candidats;
    if (c.mode === 'oppose') {
        phrase = `L'angle bleu est {{opposé par le sommet}} à l'angle vert : il mesure {{${c.m}}}°. `
            + `L'angle bleu et l'angle rouge sont des angles {{${REL_ALT}}}, donc l'angle rouge mesure {{${c.m}}}°.`;
        candidats = [
            { choix: REL_COR, code: E_ALTCOR },
            { choix: 'supplémentaire', code: E_SUPP },
            { choix: String(180 - c.m), code: E_SUPP },
        ];
    } else {
        phrase = `L'angle bleu et l'angle vert sont des angles {{${c.relation}}} : l'angle bleu mesure {{${c.m}}}°. `
            + `L'angle rouge est {{supplémentaire}} de l'angle bleu : il mesure 180° − ${c.m}° = {{${180 - c.m}}}°.`;
        candidats = [
            { choix: c.relation === REL_ALT ? REL_COR : REL_ALT, code: E_ALTCOR },
            { choix: 'opposé par le sommet', code: E_COUPLE },
            { choix: 'égal', code: E_DETOUR },
        ];
    }
    const distracteurs = filtrerDistracteurs(phrase, candidats.map((x) => x.choix));
    const texte = `<p>${enonceParalleles(c.cfg)} L'angle vert mesure ${deg(c.m)}. L'angle bleu est l'angle intermédiaire. Complète les deux étapes :</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CALCUL2), mode: 'drag', distracteurs, strict: false, elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul2_texte_trou_drag', {
        m: c.m, mode: c.mode, relation: c.relation, reponse: c.reponse, etapes: 1,
        distracteurs: candidats.filter((x) => distracteurs.includes(x.choix)),
    });
};

const genCalcul2Qcm = () => {
    const c = construireCalcul({ mode: modeCalcul2(), nommee: false });
    c.paralleles = true;
    const { correct, options } = optionsCalcul(c, SUJET_MARQUE);
    const texte = `<p>${enonceParalleles(c.cfg)} L'angle vert mesure ${deg(c.m)}. Que peut-on dire de l'angle rouge ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(L_CALCUL2), elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul2_qcm_unique', {
        m: c.m, mode: c.mode, relation: c.relation, reponse: c.reponse, etapes: 1,
        distracteurs: options.filter((o) => o.choix !== correct),
    });
};

// ── calcul_nomme : angles désignés par leur nom ──

const modeCalculNomme = () => randomChoice(['direct', 'oppose', 'supp', 'supp']);

const genCalculNommeSaisie = () => {
    const c = construireCalcul({ mode: modeCalculNomme(), nommee: true });
    const texte = `<p>Les droites ${c.cfg.nomD1} et ${c.cfg.nomD2} sont parallèles (codage sur la figure). `
        + `L'angle ${texAngle(c.nomV)} mesure ${deg(c.m)}. Combien mesure l'angle ${texAngle(c.nomR)} ? (Donne un nombre entier de degrés.)</p>`;
    const payload = makeSaisie(String(c.reponse), texte, {
        ...opts(L_CALC_NOM), strict: true, suffixe: '°', elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul_nomme_texte', {
        m: c.m, mode: c.mode, relation: c.relation, reponse: c.reponse, etapes: c.mode === 'direct' ? 0 : 1,
        nomV: c.nomV.join(''), nomR: c.nomR.join(''), erreurVisee: c.mode === 'supp' ? E_DETOUR : E_SUPP,
    });
};

const genCalculNommeQcm = () => {
    const mode = modeCalculNomme();
    const paralleles = mode !== 'direct' || randInt(1, 4) !== 1;
    const c = construireCalcul({ mode, nommee: true, paralleles });
    c.paralleles = paralleles;
    const { correct, options } = optionsCalcul(c, { v: texAngle(c.nomV), r: texAngle(c.nomR) });
    const texte = paralleles
        ? `<p>Les droites ${c.cfg.nomD1} et ${c.cfg.nomD2} sont parallèles (codage sur la figure). L'angle ${texAngle(c.nomV)} mesure ${deg(c.m)}. Que peut-on dire de l'angle ${texAngle(c.nomR)} ?</p>`
        : `<p>L'énoncé ne dit rien sur les droites ${c.cfg.nomD1} et ${c.cfg.nomD2}, et la figure ne porte aucun codage. L'angle ${texAngle(c.nomV)} mesure ${deg(c.m)}. Que peut-on dire de l'angle ${texAngle(c.nomR)} ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(L_CALC_NOM), elements_additionnels: imgElement(c.fig.render()),
    });
    return meta(payload, 'calcul_nomme_qcm_unique', {
        m: c.m, mode: c.mode, relation: c.relation, reponse: paralleles ? c.reponse : null, paralleles,
        etapes: c.mode === 'direct' ? 0 : 1, nomV: c.nomV.join(''), nomR: c.nomR.join(''),
        distracteurs: options.filter((o) => o.choix !== correct),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — paires (niveau 6) : désigner sur la figure
// ═══════════════════════════════════════════════════════════

/** Figure à 8 secteurs numérotés ①-④ (sommet A) et ⑤-⑧ (sommet B), parallélisme codé. */
const figHuitSecteurs = (m, s) => {
    const cfg = configUneSecante({ m, paralleles: true, s });
    const { fig, A, B, secteursA, secteursB } = cfg;
    const ordA = [...secteursA].sort((a, b) => a.bis - b.bis);
    const ordB = [...secteursB].sort((a, b) => a.bis - b.bis);
    ordA.forEach((sec, i) => fig.numeroSecteur(A, sec.bis, CERCLES[i], rNumeroAuto(sec.mesure)));
    ordB.forEach((sec, i) => fig.numeroSecteur(B, sec.bis, CERCLES[i + 4], rNumeroAuto(sec.mesure)));
    return { ...cfg, ordA, ordB };
};

const genPairesQcm = () => {
    const m = tirerMesure();
    const relation = randomChoice([REL_ALT, REL_COR]);
    const { fig, ordA, ordB, secteursB } = figHuitSecteurs(m, salt(m, randInt(0, 9999)));
    const candidatsQ = ordA.map((sec, i) => ({ sec, i })).filter(({ sec }) => relation === REL_COR || sec.interior);
    const { sec: secQ, i: idxQ } = randomChoice(candidatsQ);
    const cible = relation === REL_ALT ? secteurAlterneInterne(secQ, secteursB) : secteurCorrespondant(secQ, secteursB);
    const correct = CERCLES[ordB.indexOf(cible) + 4];
    const autre = secteurAutreRelation(secQ, secteursB, relation);
    const distracteurs = ordB.map((sec, j) => ({ sec, choix: CERCLES[j + 4] }))
        .filter(({ sec }) => sec !== cible)
        .map(({ sec, choix }) => ({
            choix,
            code: sec === autre ? E_ALTCOR : (sec.sideT === cible.sideT ? E_SUPP : E_COUPLE),
        }));
    const texte = `<p>Les droites (d₁) et (d₂) sont parallèles (codage sur la figure). Quel angle du sommet B forme, avec l'angle ${CERCLES[idxQ]}, un couple d'angles <b>${relation}</b> ?</p>`;
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.choix)], correct, texte, {
        ...opts(L_PAIRES), elements_additionnels: imgElement(fig.render()),
    });
    return meta(payload, 'paires_qcm_unique', { m, relation, question: CERCLES[idxQ], correct, distracteurs });
};

const genPairesRelier = () => {
    const m = tirerMesure();
    const relation = randomChoice([REL_ALT, REL_COR]);
    const { fig, ordA, ordB, secteursB } = figHuitSecteurs(m, salt(m, randInt(0, 9999)));
    const numB = (sec) => CERCLES[ordB.indexOf(sec) + 4];
    let pairs, leurres = [];
    if (relation === REL_COR) {
        pairs = ordA.map((sec, i) => ({ source: CERCLES[i], target: numB(secteurCorrespondant(sec, secteursB)) }));
    } else {
        const sources = ordA.map((sec, i) => ({ sec, i })).filter(({ sec }) => sec.interior);
        pairs = sources.map(({ sec, i }) => ({ source: CERCLES[i], target: numB(secteurAlterneInterne(sec, secteursB)) }));
        const cibles = new Set(pairs.map((p) => p.target));
        leurres = ordB.filter((sec) => !cibles.has(numB(sec)))
            .map((sec) => ({ target: numB(sec), code: sources.some(({ sec: q }) => secteurCorrespondant(q, secteursB) === sec) ? E_ALTCOR : E_COUPLE }));
    }
    const texte = relation === REL_COR
        ? '<p>Les droites (d₁) et (d₂) sont parallèles (codage sur la figure). Relie chaque angle du sommet A à l\'angle <b>correspondant</b> du sommet B.</p>'
        : '<p>Les droites (d₁) et (d₂) sont parallèles (codage sur la figure). Relie chacun des deux angles intérieurs du sommet A à son angle <b>alterne interne</b> du sommet B. Deux cibles ne servent pas.</p>';
    const payload = makeRelier(pairs, texte, {
        ...opts(L_PAIRES), elements_additionnels: imgElement(fig.render()),
        ...(leurres.length ? { cibles_leurres: leurres } : {}),
    });
    return meta(payload, 'paires_relier', { m, relation, distracteurs: leurres.map((l) => ({ choix: l.target, code: l.code })) });
};

// ═══════════════════════════════════════════════════════════
// Carte 8 — nommer (niveau 7) : nom à trois lettres
// ═══════════════════════════════════════════════════════════

const construireNommer = () => {
    const m = tirerMesure();
    const relation = randomChoice([REL_ALT, REL_COR]);
    const cfg = configUneSecante({ m, nommee: true, paralleles: true, s: salt(m, randInt(0, 9999)) });
    const { secteursA, secteursB } = cfg;
    const sV = randomChoice(secteursDe(secteursA, { interior: relation === REL_ALT ? true : null }));
    const cible = relation === REL_ALT ? secteurAlterneInterne(sV, secteursB) : secteurCorrespondant(sV, secteursB);
    const autre = secteurAutreRelation(sV, secteursB, relation);
    // L'opposé par le sommet de la cible EST l'angle de l'autre relation (propriété du cours) :
    // le servir en plus donnerait deux fois le même angle dans le QCM, écrit dans les deux sens,
    // et compterait faux une réponse juste (A1). Le quatrième choix est donc un ADJACENT de la
    // cible, c'est-à-dire son supplémentaire : l'erreur « confondre égaux et supplémentaires ».
    const oppose = secteurOppose(cible, secteursB);
    const supp = randomChoice(secteursB.filter((s) => s !== cible && s !== oppose));
    const nomV = nomSecteur(sV, cfg.rayonsA, cfg.lettres.A);
    const nomC = nomSecteur(cible, cfg.rayonsB, cfg.lettres.B);
    return {
        cfg, m, relation, nomV, nomC, cible,
        nomAutre: nomSecteur(autre, cfg.rayonsB, cfg.lettres.B),
        nomSupp: supp ? nomSecteur(supp, cfg.rayonsB, cfg.lettres.B) : null,
    };
};

/**
 * TOUTES les ecritures a trois lettres qui designent le secteur.
 *
 * Un angle se nomme avec un point de chaque cote, et un cote peut porter PLUSIEURS
 * points nommes : sur la figure, le rayon qui part de S vers J se prolonge jusqu'a
 * X, donc CSJ et CSX sont le meme angle. Le generateur n'acceptait que le premier
 * et comptait l'autre faux (retour prof 2026-09-26).
 */
const nomsSecteur = (sec, rayons, sommet) => {
    const r1 = rayons.find((r) => memeDir(r.dir, sec.a1));
    const r2 = rayons.find((r) => memeDir(r.dir, sec.a2));
    if (!r1 || !r2) throw new Error('953 : secteur sans rayons nommes');
    const out = [];
    (r1.ls || [r1.l]).forEach((x) => (r2.ls || [r2.l]).forEach((y) => {
        out.push(`${x}${sommet}${y}`);
        out.push(`${y}${sommet}${x}`);
    }));
    return [...new Set(out)];
};

/** Un angle à trois lettres, réduit à son identité : le sommet, puis les deux autres lettres triées. */
const angleCanon = ([a, s, b]) => `${s}|${[a, b].sort().join('')}`;

const genNommerSaisie = () => {
    const n = construireNommer();
    const rel = n.relation === REL_ALT ? 'alterne interne' : 'correspondant';
    const texte = `<p>Les droites ${n.cfg.nomD1} et ${n.cfg.nomD2} sont parallèles (codage sur la figure). `
        + `Nomme, à trois lettres, l'angle ${rel} de l'angle ${texAngle(n.nomV)}. Écris seulement les trois lettres, le sommet au milieu (exemple : XYZ).</p>`;
    const canon = n.nomC.join('');
    // Toutes les ecritures du MEME angle sont recevables : les deux sens de lecture,
    // et chaque point nomme de chaque cote.
    const admises = nomsSecteur(n.cible, n.cfg.rayonsB, n.cfg.lettres.B).filter((x) => x !== canon);
    const payload = makeSaisie(canon, texte, {
        ...opts(L_NOMMER), strict: true, comparaison: 'expression', reponses_acceptees: admises,
        elements_additionnels: imgElement(n.cfg.fig.render()),
    });
    return meta(payload, 'nommer_texte', { m: n.m, relation: n.relation, nomV: n.nomV.join(''), reponse: canon, erreurVisee: E_SOMMET });
};

const genNommerQcm = () => {
    // Quatre choix qui désignent quatre angles RÉELLEMENT différents : on compare les identités
    // (sommet + les deux autres lettres), jamais les chaînes, car un même angle s'écrit dans les
    // deux sens. Un tirage qui n'y parvient pas est rejoué en entier, jamais rattrapé à
    // l'assemblage. Le repli (dernier tirage retenu) est atteignable mais n'a jamais servi sur
    // 5 000 tirages : les trois secteurs sont distincts par construction.
    let n = construireNommer();
    let options = null;
    for (let essai = 0; essai < 40; essai++) {
        const candidat = [
            { choix: texAngle(n.nomC), code: null, id: angleCanon(n.nomC) },
            { choix: texAngle(sommetMalPlace(n.nomC)), code: E_SOMMET, id: `malplace:${angleCanon(n.nomC)}` },
            { choix: texAngle(n.nomAutre), code: E_ALTCOR, id: angleCanon(n.nomAutre) },
            n.nomSupp ? { choix: texAngle(n.nomSupp), code: E_SUPP, id: angleCanon(n.nomSupp) } : null,
        ];
        if (candidat.every(Boolean)
            && new Set(candidat.map((o) => o.id)).size === 4
            && new Set(candidat.map((o) => o.choix)).size === 4) {
            options = candidat;
            break;
        }
        n = construireNommer();
    }
    if (!options) {
        options = [
            { choix: texAngle(n.nomC), code: null },
            { choix: texAngle(sommetMalPlace(n.nomC)), code: E_SOMMET },
            { choix: texAngle(n.nomAutre), code: E_ALTCOR },
        ];
    }
    const rel = n.relation === REL_ALT ? 'alterne interne' : 'correspondant';
    const correct = options[0].choix;
    const texte = `<p>Les droites ${n.cfg.nomD1} et ${n.cfg.nomD2} sont parallèles (codage sur la figure). `
        + `Quel est l'angle ${rel} de l'angle ${texAngle(n.nomV)} ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(L_NOMMER), elements_additionnels: imgElement(n.cfg.fig.render()),
    });
    return meta(payload, 'nommer_qcm_unique', {
        m: n.m, relation: n.relation, nomV: n.nomV.join(''), reponse: n.nomC.join(''),
        distracteurs: options.filter((o) => o.choix !== correct),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 9 — trapeze (niveau 8) : synthèse, deux sécantes
// ═══════════════════════════════════════════════════════════

/**
 * Deux droites (d₁) haut et (d₂) bas, deux sécantes. Sur la première : deux
 * angles alternes internes marqués m1 (verts) → parallélisme à établir. Sur la
 * seconde : angle bleu m2 donné en bas, angle rouge cherché en haut (alterne
 * interne ou correspondant, zéro étape). Aucun codage de parallélisme : il est
 * à établir. Les sécantes ne se coupent pas entre les deux droites.
 */
const construireTrapeze = () => {
    const dirL = randInt(-8, 8);
    const m1 = tirerMesure();
    const D1 = tirerDistance(m1);
    const h = D1 * UNITE * Math.sin(rad(m1));                 // hauteur de la bande
    const cot = (a) => Math.cos(rad(a)) / Math.sin(rad(a));
    let m2 = null;
    for (let i = 0; i < 60 && m2 == null; i++) {
        const cand = tirerMesure();
        if (cand === m1 || cand === 180 - m1) continue;
        const D2 = h / (UNITE * Math.sin(rad(cand)));
        if (D2 < 3 || D2 > 6) continue;
        const dx1 = h * cot(m1), dx2 = h * cot(cand);
        if (100 + dx2 - dx1 < 40) continue;                    // les sommets du haut restent ordonnés
        if (100 + Math.abs(dx1) + Math.abs(dx2) > 290) continue;  // cadre
        m2 = cand;
    }
    if (m2 == null) return null;
    const relation = randomChoice([REL_ALT, REL_COR]);
    const fig = creerFigure(VB, salt(m1, m2, dirL, randInt(0, 9999)));
    // Points : C1, C2 sur (d₂), A1, A2 sur (d₁) ; bande centrée.
    const dx1 = h * cot(m1), dx2 = h * cot(m2);
    const xs = [0, 100, dx1, 100 + dx2];
    const decal = VB.w / 2 - (Math.min(...xs) + Math.max(...xs)) / 2;
    const yBas = VB.h / 2 + h / 2, yHaut = VB.h / 2 - h / 2;
    const C1 = { x: decal, y: yBas }, C2 = { x: decal + 100, y: yBas };
    // (d₂) passe par C1 avec l'inclinaison dirL ; (d₁) est parallèle à h au-dessus.
    const A1 = inter(C1, dirL + m1, { x: C1.x, y: yHaut }, dirL);
    const A2 = inter(C2, dirL + m2, { x: C2.x, y: yHaut }, dirL);
    fig.droite(C1, dirL, 'ln2');
    fig.droite(A1, dirL, 'ln1');
    fig.droite(C1, dirL + m1, 'tr1');
    fig.droite(C2, dirL + m2, 'tr2');
    fig.point(A1, 'A', 90, 14); fig.point(A2, 'B', 90, 14);
    fig.point(C1, 'C', -90, 14); fig.point(C2, 'D', -90, 14);
    etiquetteBout(fig, A1, dirL + 180, '(d₁)');
    etiquetteBout(fig, C1, dirL + 180, '(d₂)');
    const angleVers = (P, Q) => Math.atan2(-(Q.y - P.y), Q.x - P.x) * 180 / Math.PI;
    const sC1 = secteursAutour(dirL, dirL + m1, angleVers(C1, A1));
    const sA1 = secteursAutour(dirL, dirL + m1, angleVers(A1, C1));
    const sC2 = secteursAutour(dirL, dirL + m2, angleVers(C2, A2));
    const sA2 = secteursAutour(dirL, dirL + m2, angleVers(A2, C2));
    // Sécante 1 : alternes internes m1 en C et en A (le côté intérieur opposé à la sécante 2).
    const v1 = secteursDe(sC1, { interior: true, mesure: m1 })[0];
    const v1b = secteurAlterneInterne(v1, sA1);
    // Sécante 2 : bleu m2 en D (intérieur), rouge en B.
    const v2 = secteursDe(sC2, { interior: true, mesure: m2 })[0];
    const r2 = relation === REL_ALT ? secteurAlterneInterne(v2, sA2) : secteurCorrespondant(v2, sA2);
    if (!v1 || !v1b || !v2 || !r2) return null;
    if (Math.round(r2.mesure) !== m2) throw new Error('953 trapèze : la figure ne réalise pas m2');
    arc(fig, C1, v1, deg(m1), STYLE.donne, { r: 18 });
    arc(fig, A1, v1b, deg(m1), STYLE.donne, { r: 18 });
    arc(fig, C2, v2, deg(m2), STYLE.inter, { r: 18 });
    arc(fig, A2, r2, '?', STYLE.cherche, { r: 18 });
    return { fig, m1, m2, relation, reponse: m2 };
};

const tirerTrapeze = () => {
    for (let i = 0; i < 40; i++) { const t = construireTrapeze(); if (t) return t; }
    throw new Error('953 trapèze : aucun tirage valide');
};

const ENONCE_TRAPEZE = '<p>Les droites (d₁) et (d₂) sont coupées par deux sécantes. Les angles verts, en A et en C, mesurent tous deux ';

const genTrapezeDrag = () => {
    const t = tirerTrapeze();
    const phrase = `Les angles verts sont des angles {{${REL_ALT}}} de même mesure, donc les droites (d₁) et (d₂) sont {{parallèles}}. `
        + `L'angle rouge et l'angle bleu sont alors des angles {{${t.relation}}}, donc l'angle rouge mesure {{${t.m2}}}°.`;
    const candidats = [
        { choix: t.relation === REL_ALT ? REL_COR : REL_ALT, code: E_ALTCOR },
        { choix: 'sécantes', code: E_ASPECT },
        { choix: String(180 - t.m2), code: E_SUPP },
        { choix: String(t.m1), code: E_COUPLE },
    ];
    const distracteurs = filtrerDistracteurs(phrase, candidats.map((x) => x.choix));
    const texte = `${ENONCE_TRAPEZE}${deg(t.m1)} ; l'angle bleu mesure ${deg(t.m2)}. Complète la démonstration en deux temps :</p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_TRAPEZE), mode: 'drag', distracteurs, strict: false, elements_additionnels: imgElement(t.fig.render()),
    });
    return meta(payload, 'trapeze_texte_trou_drag', {
        m1: t.m1, m2: t.m2, relation: t.relation, reponse: t.reponse,
        distracteurs: candidats.filter((x) => distracteurs.includes(x.choix)),
    });
};

const genTrapezeSaisie = () => {
    const t = tirerTrapeze();
    // La consigne demandait d'abord d'EXPLIQUER pourquoi les droites sont parallèles,
    // alors que le seul champ de réponse est un nombre de degrés : on demandait un
    // travail qu'on ne recueillait pas (retour prof 2026-09-26). La spec réserve la
    // justification rédigée à la SORTIE PAPIER, où un cadre lui est destiné ; à
    // l'écran, elle décrit ce format comme « saisie numérique d'une mesure en degrés ».
    // Le raisonnement sur le parallélisme reste exigé par les deux autres formats de
    // la carte : le drag le fait construire pas à pas, le QCM le met dans les options.
    const texte = `${ENONCE_TRAPEZE}${deg(t.m1)} ; l'angle bleu mesure ${deg(t.m2)}. `
        + 'Quelle est la mesure de l\'angle rouge (nombre entier de degrés) ?</p>';
    const payload = makeSaisie(String(t.reponse), texte, {
        ...opts(L_TRAPEZE), strict: true, suffixe: '°', elements_additionnels: imgElement(t.fig.render()),
    });
    return meta(payload, 'trapeze_texte', { m1: t.m1, m2: t.m2, relation: t.relation, reponse: t.reponse, erreurVisee: E_SUPP });
};

const genTrapezeQcm = () => {
    const t = tirerTrapeze();
    const correct = `${deg(t.m2)} : les angles verts alternes internes sont égaux, donc (d₁) et (d₂) sont parallèles ; l'angle rouge et l'angle bleu sont alors des angles ${t.relation}, donc égaux.`;
    const options = [
        { choix: correct, code: null },
        { choix: `${deg(180 - t.m2)} : l'angle rouge est le supplémentaire de l'angle bleu.`, code: E_SUPP },
        { choix: `${deg(t.m1)} : l'angle rouge est égal aux angles verts.`, code: E_COUPLE },
        { choix: 'On ne peut pas le savoir : rien ne dit que les droites (d₁) et (d₂) sont parallèles.', code: E_SENS },
    ];
    const texte = `${ENONCE_TRAPEZE}${deg(t.m1)} ; l'angle bleu mesure ${deg(t.m2)}. Que peut-on dire de l'angle rouge ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, {
        ...opts(L_TRAPEZE), elements_additionnels: imgElement(t.fig.render()),
    });
    return meta(payload, 'trapeze_qcm_unique', {
        m1: t.m1, m2: t.m2, relation: t.relation, reponse: t.reponse,
        distracteurs: options.filter((o) => o.choix !== correct),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 10 — caracterisation : les deux implications et l'équivalence
// ═══════════════════════════════════════════════════════════

const genCaracterisationDrag = () => {
    const m = tirerMesure();
    const relation = randomChoice([REL_ALT, REL_COR]);
    const cfg = configUneSecante({ m, s: salt(m, randInt(0, 9999)) });
    const { fig, A, B, secteursA, secteursB } = cfg;
    const sV = randomChoice(secteursDe(secteursA, { interior: relation === REL_ALT ? true : null }));
    const sR = relation === REL_ALT ? secteurAlterneInterne(sV, secteursB) : secteurCorrespondant(sV, secteursB);
    arc(fig, A, sV, '', STYLE.neutre, { r: 20 });
    arc(fig, B, sR, '', STYLE.second, { r: 20 });
    const phrase = `Sur la figure, l'angle vert et l'angle rouge sont des angles {{${relation}}}. `
        + 'Si les droites (d₁) et (d₂) sont parallèles, alors ces deux angles ont la {{même}} mesure. '
        + 'Réciproquement, si ces deux angles ont la même mesure, alors les droites (d₁) et (d₂) sont {{parallèles}}. '
        + 'Ces deux implications forment une {{équivalence}} : l\'égalité des angles caractérise le parallélisme.';
    const candidats = [
        { choix: relation === REL_ALT ? REL_COR : REL_ALT, code: E_ALTCOR },
        { choix: 'somme', code: E_SUPP },
        { choix: 'sécantes', code: E_ASPECT },
        { choix: 'définition', code: null },
    ];
    const distracteurs = filtrerDistracteurs(phrase, candidats.map((x) => x.choix));
    const texte = '<p>Les droites (d₁) et (d₂) sont coupées par la sécante (s). Complète l\'énoncé de la propriété caractéristique :</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CARACT), mode: 'drag', distracteurs, strict: false, elements_additionnels: imgElement(fig.render()),
    });
    return meta(payload, 'caracterisation_texte_trou_drag', {
        m, relation, distracteurs: candidats.filter((x) => distracteurs.includes(x.choix)),
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    position_qcm_unique: genPositionQcm,
    reciproque_qcm_unique: genReciproqueQcm,
    reciproque_texte_trou_drag: genReciproqueDrag,
    decision_nommee_qcm_unique: genDecisionNommeeQcm,
    decision_nommee_texte_trou_drag: genDecisionNommeeDrag,
    calcul1_texte: genCalcul1Saisie,
    calcul1_texte_trou_drag: genCalcul1Drag,
    calcul1_qcm_unique: genCalcul1Qcm,
    calcul2_texte: genCalcul2Saisie,
    calcul2_texte_trou_drag: genCalcul2Drag,
    calcul2_qcm_unique: genCalcul2Qcm,
    calcul_nomme_texte: genCalculNommeSaisie,
    calcul_nomme_qcm_unique: genCalculNommeQcm,
    paires_qcm_unique: genPairesQcm,
    paires_relier: genPairesRelier,
    nommer_texte: genNommerSaisie,
    nommer_qcm_unique: genNommerQcm,
    trapeze_texte_trou_drag: genTrapezeDrag,
    trapeze_texte: genTrapezeSaisie,
    trapeze_qcm_unique: genTrapezeQcm,
    caracterisation_texte_trou_drag: genCaracterisationDrag,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', texte_trou_drag: 'Drag', relier: 'Relier',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'position', label: L_POSITION, formatsList: buildFormats('position') },
    { id: 'reciproque', label: L_RECIP, formatsList: buildFormats('reciproque') },
    { id: 'decision_nommee', label: L_DECISION, formatsList: buildFormats('decision_nommee') },
    { id: 'calcul1', label: L_CALCUL1, formatsList: buildFormats('calcul1') },
    { id: 'calcul2', label: L_CALCUL2, formatsList: buildFormats('calcul2') },
    { id: 'calcul_nomme', label: L_CALC_NOM, formatsList: buildFormats('calcul_nomme') },
    { id: 'paires', label: L_PAIRES, formatsList: buildFormats('paires') },
    { id: 'nommer', label: L_NOMMER, formatsList: buildFormats('nommer') },
    { id: 'trapeze', label: L_TRAPEZE, formatsList: buildFormats('trapeze') },
    { id: 'caracterisation', label: L_CARACT, formatsList: buildFormats('caracterisation') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genPositionQcm, genReciproqueQcm, genReciproqueDrag, genDecisionNommeeQcm, genDecisionNommeeDrag,
    genCalcul1Saisie, genCalcul1Drag, genCalcul1Qcm, genCalcul2Saisie, genCalcul2Drag, genCalcul2Qcm,
    genCalculNommeSaisie, genCalculNommeQcm, genPairesQcm, genPairesRelier, genNommerSaisie, genNommerQcm,
    genTrapezeDrag, genTrapezeSaisie, genTrapezeQcm, genCaracterisationDrag,
    tirerMesure, configUneSecante, construireDecision, construireCalcul, construireTrapeze,
};
